import type {Option as OptionType} from '@/modules/common/components/RegionSelect/hooks/useRegionSelectOptions';
import type {OptionTypeBase, Styles} from 'react-select';

import {useTheme} from '@emotion/react';
import styled from '@emotion/styled';
import {IconChevronBottom} from '@travel-frontend/icons';
import {Select} from '@travel-frontend/ui-components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {MenuList} from '@/modules/common/components/RegionSelect/components/MenuList';
import {Option} from '@/modules/common/components/RegionSelect/components/Option';
import {useRegionSelectOptions} from '@/modules/common/components/RegionSelect/hooks/useRegionSelectOptions';
import {useQueryParams} from '@/modules/common/context/QueryParams';
import {useEnvironment} from '@/modules/common/hooks/useEnvironment';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {testId} from '@/utils/testId';

export const RegionSelect = styled(({...restProps}) => {
  const {t} = useTranslation('general');
  const [queryParams, setQueryParams] = useQueryParams();
  const {areRegionsNested, data} = useRegionSelectOptions();
  const {dispatchGenericTrackingEvent} = useTracking();
  const {isMobile} = useEnvironment();
  const theme = useTheme();

  const allRegionsOption = useMemo(
    () => ({
      label: {name: t('regionSelect.allRegions'), type: 'reset'},
      value: null,
      isDisabled: queryParams.destinationIdFilter === queryParams.destinationId,
    }),
    [t, queryParams]
  );

  const options = [allRegionsOption, ...data];

  const activeOption = queryParams.destinationIdFilter
    ? options.find(({value}) => value === queryParams.destinationIdFilter)
    : allRegionsOption;

  const handleChange = (option: OptionType) => {
    setQueryParams((previous) => ({
      ...previous,
      destinationIdFilter: option.value,
    }));

    const isOptionValueCurrentDestination = option.label.type === 'reset';

    dispatchGenericTrackingEvent({
      event: 'filter',
      event_action: 'region_filter_set',
      region_filter_input: isOptionValueCurrentDestination
        ? ['']
        : [option.value],
    });
  };

  const styles: Styles<OptionTypeBase, boolean> = {
    menuPortal: (provided) => ({
      ...provided,
      zIndex: theme.stacking.zIndexControlsPriority,
    }),
    menu: (provided) => ({...provided, width: 'max-content'}),
    menuList: (provided) => ({
      ...provided,
      width: '300px',
      margin: '4px 0',
    }),
  };

  return (
    <Select
      {...restProps}
      // @ts-expect-error ...
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldBlockScroll={!isMobile}
      value={activeOption}
      options={options}
      groupLayout={areRegionsNested}
      onChange={handleChange}
      components={{Option, MenuList}}
      styles={styles}
      isSearchable={false}
      dropdownIndicatorIcon={<IconChevronBottom />}
      formatSingleValue={(state) => ({
        value: state.data.label.name,
      })}
      highlightBehaviour="always"
      data-testid={testId('regionSelect')}
    />
  );
})``;
